import { toRaw } from 'vue'
import L from 'leaflet'
import { clone, find, findIndex, sortBy } from 'lodash-unified'
// @ts-ignore: 7016
import leafletPolycolor from 'leaflet-polycolor'
import { type MyMap } from '@map-sdk/sdk/ShipxyAPISDK'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import { GPS } from '@map-sdk/sdk/utils/GPS'
import { CommUtils } from '@map-sdk/sdk/utils/CommUtils'
import { Douglas } from '@map-sdk/sdk/utils/Douglas'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import 'leaflet-polylinedecorator'
import 'leaflet-textpath'
leafletPolycolor(L)

const layer = {
  //轨迹的路线对象
  route: null as any, // Leaflet.LayerGroup
}

/**
 * 箭头延伸
 */
const ArrowHeadExtend = L.Symbol.ArrowHead.extend({
  _buildArrowPath(layer: any, point: any) {
    const degToRad = Math.PI / 180
    const projectedPoint = point.project(layer.latLng)
    const angleInRadians = -(layer.heading - 90) * degToRad
    const halfHeadAngle = (this.options.headAngle / 2) * degToRad
    const angle1 = angleInRadians + halfHeadAngle
    const angle2 = angleInRadians - halfHeadAngle
    const p1 = new L.Point(
      projectedPoint.x - this.options.pixelSize * Math.cos(angle1),
      projectedPoint.y + this.options.pixelSize * Math.sin(angle1)
    )
    const p2 = new L.Point(
      projectedPoint.x - this.options.pixelSize * Math.cos(angle2),
      projectedPoint.y + this.options.pixelSize * Math.sin(angle2)
    )
    const p3 = new L.Point(
      p1.x - 0.5 * (p1.x - p2.x),
      p1.y - 0.5 * (p1.y - p2.y)
    )
    const p4 = new L.Point(
      p3.x - 0.3 * (p3.x - projectedPoint.x),
      p3.y - 0.3 * (p3.y - projectedPoint.y)
    )
    return [
      point.unproject(p4),
      point.unproject(p1),
      layer.latLng,
      point.unproject(p2),
    ]
  },
})

const arrowHeadExtend = (map: any) => new ArrowHeadExtend(map)
interface DataObj {
  diluteMaxCount: any
  trackid: string
  lineColor?: string
  lineWeight?: number
  dash?: boolean
  dashArray?: number[]
  circleOverColor?: string
  textColor?: string
  emission?: any //
  emissionShow?: boolean
  aiDetection?: any
  aiDetectionShow?: boolean
  startShow?: boolean
  endShow?: boolean
  middleShow?: boolean
  stopShow?: boolean
  slowShow: boolean
  suspectStopShow: boolean
  directionShow?: boolean
  isShowLable?: boolean
  isOpenMathColor?: boolean
  isShowTip?: boolean
  isShowLineHover?: boolean
  lableFields?: string[]
  showTimeType?: string[]
  tipHideTimeOut?: number
  notDiluteZoom?: any[] //
  isDilute: boolean
  isDiluteType?: number
  startCircleType?: number
  endCircleType?: number
  startCircleColor?: string
  endCircleColor?: string
  circleColor?: string
  circleFillColor?: string
  circleLowSogColor?: string
  circleLowSogFillColor?: string
  startImg?: string
  endImg?: string
  stopImg?: string
  slowImg?: string
  suspectStopImg?: string
  analysisResult?: any //
  trackLangCount: number
  pointClick?: (...params: any) => void
  pointIconClick?: (...params: any) => void
  lableMap: { [key: string]: string }
  arcMin?: number
  zIndex: number
}

class TrackSymbolImpl {
  // 唯一标识一条轨迹
  trackid: string
  //轨迹的各个点或段的数据
  tracks: any[]
  //与轨迹相关的船只或对象的数据
  ship: any
  //地图对象
  map: any
  //是否是新轨迹
  trackNew: boolean
  //是否显示轨迹
  isShowTrack: boolean
  //绘制轨迹的画布对象
  canvas_track: any
  //属性控制轨迹线条的颜色、粗细、是否虚线以及虚线的样式
  lineColor: string
  lineWeight: number
  dash: boolean
  dashArray: number[]
  //控制轨迹上的点被覆盖时的颜色和文本颜色
  circleOverColor: string
  textColor: string
  //轨迹点的排放数据
  emission: any
  //控制是否显示排放数据
  emissionShow: boolean
  // ai 检测叠加图层展示
  aiDetection: any
  aiDetectionShow: boolean
  // 鼠标悬浮点击颜色
  aiActivityColor: string
  //控制是否在轨迹上显示起点、终点、中间点、停止点、慢行点和可疑停止点
  startShow: boolean
  endShow: boolean
  middleShow: boolean
  stopShow: boolean
  slowShow: boolean
  suspectStopShow: boolean
  //是否显示方向指示
  directionShow: boolean
  // 是否开启随机线条颜色
  isOpenMathColor: boolean
  //控制是否显示标签和提示信息
  isShowLable: boolean
  isShowTip: boolean
  //是否显示轨迹点的悬浮事件
  isShowLineHover: boolean
  //定义了哪些字段将显示为标签
  lableFields: string[]
  //定义了显示时间的类型
  showTimeType: string[]
  //提示信息隐藏的超时时间
  tipHideTimeOut: number
  //定义了在哪些缩放级别下不稀释轨迹点
  notDiluteZoom: any[]
  //控制是否稀释轨迹点以优化显示
  isDilute = true
  //定义了稀释的类型
  isDiluteType: number
  //分别定义起点和终点的圆圈类型
  startCircleType?: number
  endCircleType: number
  startCircleColor: string
  endCircleColor: string
  circleColor: string
  circleFillColor: string
  circleLowSogColor: string
  circleLowSogFillColor: string
  //定义了不同状态下的图标
  startImg: string
  endImg: string
  stopImg: string
  slowImg: string
  suspectStopImg: string
  //分析结果的数据
  analysisResult: any
  //之前缩放级别和经纬度
  preZoom: number
  preLatlng: any

  //轨迹的经度
  tracklang: number
  //标签缓存数组
  labelCache: any[]
  //点击轨迹点或图标时的回调函数
  pointClick: any
  pointIconClick: any
  //用于存储标签的映射
  lableMap: { [key: string]: string }
  //弧线的最小值
  arcMin: number
  //控制图层的堆叠顺序
  zIndex: number
  //可能用于显示HTML文本的模板
  showHtml_txt_templater: any
  //鼠标提示对象
  mouseTip: any
  // 地图的实例。
  _map: MyMap
  //分析结果搜索数组
  _analysisResultSearch: any[]
  //鼠标提示的定时器
  mouseTipTimer: any
  //稀释时的最大点数
  diluteMaxCount: any
  //稀释数据的缓存记录
  diluteDataCache: Record<string, any>
  //稀释ai数据的缓存记录
  aiDataCache: Record<string, any> = {}

  // 存储老的坐标点长度
  oldTrackLength = 0
  oldAiLength = 0

  constructor(map: MyMap, tracks: any[], ship: any, dataObj: DataObj) {
    console.log(dataObj, 'dataObj')
    this.trackid = dataObj.trackid
    this.tracks = tracks
    this.oldTrackLength = tracks.length
    this.ship = ship
    this._map = map
    this._map.canvas_track = this._map.canvas_track || L.canvas()
    this.trackNew = true
    this.isShowTrack = true
    this.lineColor = dataObj.lineColor || '#993333'
    this.lineWeight = dataObj.lineWeight || 1
    this.dash = dataObj.dash || false
    this.dashArray = dataObj.dashArray || [5, 5]
    this.circleOverColor = dataObj.circleOverColor || '#FC0E0E'
    this.textColor = dataObj.textColor || '#000'
    this.emission = !dataObj.emission ? null : dataObj.emission
    this.emissionShow = dataObj.emission
    this.aiDetection = !dataObj.aiDetection ? null : dataObj.aiDetection
    this.aiDetectionShow = dataObj.aiDetection
    this.aiActivityColor = dataObj.aiDetection?.activityColor || '#f70006'
    this.oldAiLength = dataObj.aiDetection?.datas?.length || 0
    this.startShow = dataObj.startShow ?? true
    this.endShow = dataObj.endShow ?? true
    this.middleShow = dataObj.middleShow ?? true
    this.stopShow = dataObj.stopShow ?? true
    this.slowShow = dataObj.slowShow ?? true
    this.suspectStopShow = dataObj.suspectStopShow && dataObj.slowShow
    this.directionShow = dataObj.directionShow ?? true
    this.isShowLable = dataObj.isShowLable ?? true
    this.isOpenMathColor = dataObj.isOpenMathColor ?? false
    this.isShowTip = dataObj.isShowTip ?? true
    this.isShowLineHover = dataObj.isShowLineHover ?? false
    this.lableFields = dataObj.lableFields || [
      'name',
      'mmsi',
      'sog',
      'cog',
      'utc',
      'kn',
    ]
    this.showTimeType = dataObj.showTimeType || []
    this.tipHideTimeOut = dataObj.tipHideTimeOut || 2000
    this.notDiluteZoom = dataObj.notDiluteZoom || []
    this.isDilute = dataObj.isDilute == null || dataObj.isDilute
    this.isDiluteType = dataObj.isDiluteType || 1
    this.diluteDataCache = {}
    this.aiDataCache = {}
    this.diluteMaxCount = dataObj.diluteMaxCount || 300
    this.startCircleType = dataObj.startCircleType || 1
    this.endCircleType = dataObj.endCircleType || 1
    this.startCircleColor = dataObj.startCircleColor || '#06c84a'
    this.endCircleColor = dataObj.endCircleColor || '#f70006'
    this.circleColor = dataObj.circleColor || this.lineColor || '#993333'
    this.circleFillColor = dataObj.circleFillColor || '#fff'
    this.circleLowSogColor = dataObj.circleLowSogColor || '#993333'
    this.circleLowSogFillColor = dataObj.circleLowSogFillColor || '#fff'
    this.startImg = dataObj.startImg || ShipxyOptions.data_img.track_star
    this.endImg = dataObj.endImg || ShipxyOptions.data_img.track_end
    this.stopImg = dataObj.stopImg || ShipxyOptions.data_img.track_stop
    this.slowImg = dataObj.slowImg || ShipxyOptions.data_img.track_slow
    this.suspectStopImg =
      dataObj.suspectStopImg || ShipxyOptions.data_img.track_suspectStop
    this.analysisResult = Object.assign(
      {
        stops: [],
        slows: [],
        suspectStops: [],
      },
      dataObj.analysisResult
    )
    this.preZoom = 0
    this.preLatlng = {}
    this.tracklang = dataObj.trackLangCount
    this.labelCache = []
    this.pointClick = dataObj.pointClick
    this.pointIconClick = dataObj.pointIconClick
    this.lableMap = dataObj.lableMap
    this.arcMin = dataObj.arcMin || 300
    this.zIndex = dataObj.zIndex
    this._analysisResultSearch = []
    this.showHtml_txt_templater = {
      name: `船名：{1}<br/>`,
      mmsi: `MMSI：{1}<br/>`,
      sog: `航速：{1}节<br/>`,
      cog: `航迹向：{1}度<br/>`,
      utc: `时间：{1}<br/>`,
      kn: `距离起点：{1}海里<br/>`,
      ed: `距离终点：{1}海里<br/>`,
      md: `下一段航程：{1}海里<br/>`,
      draught: `吃水：{1}米<br/>`,
      dest: `目的地：{1}<br/>`,
      eta: `预到时间：{1}<br/>`,
      navistatus: `状态：{1}<br/>`,
      rot: `旋转角速度：{1}度/秒<br/>`,
      hdg: `航首向：{1}度<br/>`,
      lat: `纬度：{1}<br/>`,
      lng: `经度：{1}<br/>`,
      sog2: `两点航速：{1}节<br/>`,
      co2: `累计碳排放：{1}吨<br/>`,
    }
    this.mouseTip = {
      show(
        event: { originalEvent: any },
        content: any,
        offsetX: any,
        offsetY: any,
        defaultOffsetY: any,
        width: any
      ) {
        let legendElement = this._tooltipLegend

        if (!legendElement) {
          legendElement = L.DomUtil.create(
            'div',
            'track_mouse_tip',
            document.body
          )
          legendElement.style.zIndex = '1000'

          L.DomUtil.create(
            'div',
            'one',
            legendElement
          ).style.width = `${width}px`
          this._tooltipLegend = legendElement
        }

        const mousePosition = this.getMousePos(event.originalEvent)

        legendElement.style.left = `${mousePosition.x + (offsetX || 0)}px`
        legendElement.style.top = `${mousePosition.y + (offsetY || 5)}px`

        legendElement.childNodes[0].innerHTML = content
        legendElement.style.display = 'block'
      },

      hide() {
        if (this._tooltipLegend) {
          this._tooltipLegend.style.display = 'none'
        }
      },

      getMousePos(event: any) {
        const originalEvent = window.event || event,
          docScrollLeft =
            document.documentElement.scrollLeft || document.body.scrollLeft,
          docScrollTop =
            document.documentElement.scrollTop || document.body.scrollTop

        return {
          x: originalEvent.pageX || originalEvent.clientX + docScrollLeft,
          y: originalEvent.pageY || originalEvent.clientY + docScrollTop,
        }
      },
    }
  }
  private _Event = {
    moveend: () => this.showTrack(),
    baselayerchange: () => this._on_baselayerchange,
  }
  show() {
    if (this) {
      this._map.off('moveend', this._Event.moveend)
      this.isShowTrack = true
      this.showTrack()
      this._map.on('moveend', this._Event.moveend)
      this._map.on('baselayerchange', this._Event.baselayerchange)
    }
  }
  hide() {
    this.isShowTrack = false
    this.removeTrack()
  }
  setZIndex(zIndex: any) {
    if (this._map.canvas_track && this._map.canvas_track._container) {
      this._map.canvas_track._container.style.zIndex = zIndex
      return
    }
    console.error('canvas_track is null')
  }
  removeTrack() {
    if (layer.route) {
      layer.route.clearLayers()
      layer.route.remove()
      layer.route = null
      this.tracks = []
      if (this.aiDetection?.datas) this.aiDetection.datas = []
      this.oldTrackLength = 0
      this.oldAiLength = 0
      this.isShowTrack = false
    }
    this._map.off('moveend', this.showTrack, this)
  }
  /**航线绘制,只读模态展示， */
  showTrack() {
    let startTime = Date.now()

    // 初始化标签缓存和路线图层
    this.labelCache.length = 0
    layer.route
      ? layer.route.clearLayers()
      : (layer.route = L.featureGroup().addTo(this._map))

    // 如果没有轨迹数据或者轨迹显示未开启，则不执行
    if (this.isShowTrack && this.tracks.length > 0) {
      // 计算绘制轨迹的点
      const processedData = this.calcProcessedData()
      // 显示ai检测船舶数据
      if (this.aiDetectionShow && this.aiDetection.show && this.oldAiLength) {
        this._showTrackAi(processedData)
      }
      this.drawLine(processedData)
      startTime = Date.now() - startTime
      console.log('showTrack 耗时：', startTime)
    }
  }

  /**
   * 绘制航线
   * @param processedData
   */
  drawLine(processedData: any[]) {
    // 遍历处理后的轨迹点
    for (let i = 0; i < processedData.length; i++) {
      let currentSegment: any = null
      let directionDecorator = null
      let directionDistance = 0
      const currentDataPoint = processedData[i]
      const nextDataPoint = processedData[i + 1]
      // TODO: 多段不同颜色表示不同的气象等信息
      const lineColor = [
        '#9C27B0',
        '#0d61be',
        '#008a3e',
        '#b71b61',
        '#c5912e',
        '#69f',
        '#f00',
        '#2770d4',
        '#E91E63',
        '#00BCD4',
      ]
      const colorDraw = this.isOpenMathColor
        ? lineColor[Math.floor(Math.random() * lineColor.length)]
        : this.lineColor
      // 如果存在连续点，则绘制轨迹线和相关装饰
      if (i + 1 !== processedData.length) {
        if (currentDataPoint.join === false) continue
        let segmentPoints: L.LatLngExpression[] = [
          [currentDataPoint.lat, currentDataPoint.lng],
          [nextDataPoint.lat, nextDataPoint.lng],
        ]
        let startPoint = segmentPoints[0]
        let endPoint = segmentPoints[1]
        const startPointLayer = this._map.latLngToLayerPoint(
          L.latLng(currentDataPoint.lat, currentDataPoint.lng)
        )
        let midPointLayer = this._map.latLngToLayerPoint(
          L.latLngBounds(
            L.latLng(currentDataPoint.lat, currentDataPoint.lng),
            L.latLng(nextDataPoint.lat, nextDataPoint.lng)
          ).getCenter()
        )
        let segmentDistance = currentDataPoint.md

        Math.abs(currentDataPoint.index - nextDataPoint.index) !== 1 &&
          (segmentDistance = CanvasShipUtils.getDistance(
            [currentDataPoint.lat, currentDataPoint.lng],
            [nextDataPoint.lat, nextDataPoint.lng],
            'mn'
          ))
        if (segmentDistance >= this.arcMin) {
          segmentPoints = L.Control.PolylineMeasure.prototype._polylineArc(
            L.latLng([currentDataPoint.lat, currentDataPoint.lng]),
            L.latLng([nextDataPoint.lat, nextDataPoint.lng])
          ) as L.LatLngExpression[]
          const midIndex = segmentPoints.length / 2
          midPointLayer = this._map.latLngToLayerPoint(segmentPoints[midIndex])
          startPoint = segmentPoints[midIndex]
          endPoint = segmentPoints[midIndex + 1]
        }

        // 绘制轨迹线段
        currentSegment = L.polyline(segmentPoints, {
          renderer: this._map.canvas_track,
          color: colorDraw,
          weight: this.lineWeight,
          opacity: 1,
          dashArray: this.dash ? this.dashArray : '0',
        })

        directionDistance = startPointLayer.distanceTo(midPointLayer) + 5
        // 绘制方向指示箭头
        if (this.directionShow) {
          directionDecorator = L.polylineDecorator(
            [startPoint, endPoint] as any,
            {
              patterns: [
                {
                  offset: directionDistance,
                  symbol: arrowHeadExtend({
                    pixelSize: 15,
                    headAngle: 40,
                    pathOptions: {
                      renderer: this._map.canvas_track,
                      fillOpacity: 1,
                      weight: 0,
                      color: colorDraw,
                    },
                  }),
                },
              ],
            }
          )
        }

        if (directionDecorator) layer.route.addLayer(directionDecorator)
        if (currentSegment) layer.route.addLayer(currentSegment)
        if (currentSegment && this.isShowLineHover) {
          currentSegment
            .on(
              'mouseover',
              (e: {
                latlng: L.LatLngExpression
                target: { options: { index: number } }
              }) => {
                currentSegment.setStyle({ weight: '3' })
                // 可以在这里添加其他的交互，比如显示一个弹窗
                L.popup({
                  closeButton: false,
                  className: 'track-popup',
                })
                  .setLatLng(e.latlng)
                  .setContent(
                    this.showHtml(
                      processedData[e.target.options.index - 1],
                      processedData[e.target.options.index]
                    )
                  )
                  .openOn(toRaw(this._map))
              }
            )
            .on('mouseout', () => {
              // this.setStyle({ color: 'blue' })
              currentSegment.setStyle({ weight: '1' })
              // 关闭弹窗或者做其他的清理工作
              this._map.closePopup()
            })
        }
      }
      const isShowLab = currentDataPoint.isShowMarker ?? true
      // 绘制标签
      if (this.isShowLable && isShowLab) {
        const direction = this.getLineDir(currentDataPoint, nextDataPoint)
        let timeStr =
          (currentDataPoint.from === 1 || currentDataPoint.from === 2
            ? ' '
            : '') + CommUtils.dataFormat(currentDataPoint.utc * 1000)
        let labelStr = ''

        if (this.lableMap && this.lableMap[`${currentDataPoint.utc * 1000}`]) {
          labelStr = ` ${this.lableMap[`${currentDataPoint.utc * 1000}`]}`
        }
        timeStr += labelStr

        const labelLength = Math.max(
          CanvasShipUtils.calcStrLength(
            this._map,
            timeStr,
            '600 15px "Microsoft Yahei",Arial'
          ),
          165
        )
        const labelPath1 = this.getTrackLabelPath1(
          {
            lat: currentDataPoint.lat,
            lng: currentDataPoint.lng,
          },
          direction
        )
        const labelPath2 = this.getTrackLabelPath2(
          {
            lat: currentDataPoint.lat,
            lng: currentDataPoint.lng,
          },
          currentDataPoint.from === 1 || currentDataPoint.from === 2
            ? labelLength + 12
            : labelLength,
          direction
        )
        // 判断是否显示标牌
        if (this.addTrackManager(labelPath2)) {
          const labelPolyline = L.polyline(labelPath1 as any, {
            renderer: this._map.canvas_track,
            fillColor: '#fff',
            fillOpacity: 1,
            color: colorDraw,
            weight: 1,
            opacity: 0.7,
          })
          const labelPoints = []
          const labelStartPoint = this._map.latLngToContainerPoint(
            labelPath2[0] as any
          )
          const labelEndPoint = this._map.latLngToContainerPoint(
            labelPath2[1] as any
          )

          labelPoints.push(
            this._map.containerPointToLatLng(
              L.point(labelStartPoint.x, labelStartPoint.y - 8)
            ),
            this._map.containerPointToLatLng(
              L.point(labelEndPoint.x + 4, labelEndPoint.y - 8)
            ),
            this._map.containerPointToLatLng(
              L.point(labelEndPoint.x + 4, labelEndPoint.y + 8)
            ),
            this._map.containerPointToLatLng(
              L.point(labelStartPoint.x, labelStartPoint.y + 8)
            )
          )

          const labelBackground = L.polygon(labelPoints, {
            fillColor: '#fff',
            fillOpacity: 0.5,
            color: colorDraw,
            weight: 1,
            opacity: 1,
            lineCap: 'butt',
            lineJoin: 'bevel',
          }) as any
          //
          labelBackground.setText(timeStr, {
            repeat: false,
            offset: 12,
            attributes: {
              fill: this.textColor,
              'font-weight': '100',
              'font-size': '12px',
              'font-family': 'Microsoft Yahei',
              x: 8,
            },
          })

          // 标签指示线条
          layer.route.addLayer(labelPolyline)
          // 标签显示样式及文字
          layer.route.addLayer(labelBackground)

          if (1 == currentDataPoint.from || 2 == currentDataPoint.from) {
            const icon = {
              iconUrl:
                'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAYAAABWdVznAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAALEgAACxIB0t1+/AAAABZ0RVh0Q3JlYXRpb24gVGltZQAwNy8wOS8xMlFnxgkAAAAcdEVYdFNvZnR3YXJlAEFkb2JlIEZpcmV3b3JrcyBDUzQGstOgAAABu0lEQVQokVWQvWtTYRjFf++HiSYk9epFDbaRVKHYOFTbIeJWd1Mqmdz8QOjgIDi4BYR2qw5u9rrppoN/QFcVMkmh6lDFj1IlH03S25jkvu/rEHrFMx14zo9zeIRzjsZCkeTV8pyt7zyz7dYFhgONc6B1JD1/Q/qn7mTurdQARL08TaI0fy3a+vha+ieVLkwhsh44i23VMd+/YBu/jC5MLWYfPn0j2o/uzpqvn9+r0wWlz8+AUqQqSxxo/+UTok8fMNvfInXmXEnanZ/PxeG0krkJXNglVVkiDJYJg2UAXG8fmcsjjqS1/b0daNtpFtX4JK4X4nohALbTAqD7+EHcJDJjmB9bRU2/r7AG1x6FOtXbZKtrsY8BIWA41NpZgwu7uEE/Pu7evw7A0dVXsccaMAYNRHa3qUUiCYAXrNO6NR/DB81uOMBZE2lMtOHCzgx/dBzygnUAmpWL/0ATAWyKxo3Lc67feycSSSVSaRCS/+Tc6CH9nhHJVEm6vXZN5cYXkTJCCOQxHz0xic6fRfonRtvBqFx+4fiLtzVRL08DcOjSlVnbagRur13EmtE+qSKRzmxKz785Vl2rAfwFbCvCZDdQ2CMAAAAASUVORK5CYII=',
              iconAnchor: [-0x3, 0x6],
            }
            2 == currentDataPoint.from &&
              (icon['iconUrl'] =
                'data:image/png;base64,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')
            layer.route.addLayer(
              L.marker([labelPath1[1][0], labelPath1[1][1]], {
                icon: L.icon(icon as any),
              })
            )
          }
        }
      }
      if (currentDataPoint.isStartLatLng || currentDataPoint.isEndLatLng) {
        const icon = currentDataPoint.isStartLatLng
          ? L.icon({
              iconUrl: this.startImg,
              iconAnchor: [15, 32],
            })
          : L.icon({
              iconUrl: this.endImg,
              iconAnchor: [16, 32],
            })

        const marker = L.marker([currentDataPoint.lat, currentDataPoint.lng], {
          icon,
          _data: {
            type: currentDataPoint.isStartLatLng ? 'start' : 'end',
            track: currentDataPoint,
            trackid: this.trackid,
          },
        } as any)

        if (
          (currentDataPoint.isStartLatLng && this.startShow) ||
          (currentDataPoint.isEndLatLng && this.endShow)
        ) {
          layer.route.addLayer(marker)
          if (this.pointIconClick) {
            marker.on('click', (event) => {
              this.pointIconClick(event.target.options._data)
            })
          }
        }

        const circleOptions = {
          renderer: this._map.canvas_track,
          radius: 3,
          weight: 2,
          color: colorDraw,
          old_color: colorDraw,
          fill: true,
          fillColor: currentDataPoint.isStartLatLng
            ? this.startCircleColor
            : this.endCircleColor,
          fillOpacity: 1,
          opacity: 1,
          index: i,
          trackid: this.trackid,
        }

        if (
          (this.startCircleType != 0 && currentDataPoint.isStartLatLng) ||
          (this.endCircleType != 0 && currentDataPoint.isEndLatLng)
        ) {
          if (
            (this.startCircleType === 2 && currentDataPoint.isStartLatLng) ||
            (this.endCircleType === 2 && currentDataPoint.isEndLatLng)
          ) {
            circleOptions.color = circleOptions.old_color = this.circleColor
            circleOptions.fillColor = currentDataPoint.isStartLatLng
              ? this.startCircleColor
              : this.endCircleColor
          } else {
            circleOptions.radius = 8
            circleOptions.fillColor = '#ddd'
            circleOptions.fillOpacity = 0.5
          }
          const circleMarker = L.circleMarker(
            [currentDataPoint.lat, currentDataPoint.lng],
            circleOptions
          )

          if (this.isShowTip) {
            circleMarker.on('mouseover', (event) => {
              event.target.setStyle({
                color: this.circleOverColor,
              })

              const prevPoint = processedData[event.target.options.index - 1],
                nextPoint = processedData[event.target.options.index],
                tooltipContent = this.showHtml(
                  nextPoint,
                  prevPoint,
                  this.lableFields
                )

              this.mouseTip.show(event, tooltipContent, 13, 13, 0, 170)
              setTimeout(() => {
                this.mouseTip.hide()
              }, this.tipHideTimeOut)
            })
            circleMarker.on('mouseout', (event) => {
              event.target.setStyle({
                color: event.target.options.old_color,
              })
              this.mouseTip.hide()
            })
          }

          if (this.pointClick) {
            circleMarker.on('click', (event) => {
              const prevPoint = processedData[event.target.options.index - 1],
                nextPoint = processedData[event.target.options.index],
                clickData = this.showHtml_data(nextPoint, prevPoint)

              setTimeout(() => {
                this.pointClick(clickData, event.target.options.trackid)
              }, 60)
            })
          }

          layer.route.addLayer(circleMarker)
        }
      } else {
        const analysisInfoChanged = currentDataPoint.analysisInfo
          ? currentDataPoint.analysisInfo.type
          : ''
        const nextAnalysisInfoChanged = nextDataPoint.analysisInfo
          ? nextDataPoint.analysisInfo.type
          : ''
        console.log(
          analysisInfoChanged,
          nextAnalysisInfoChanged,
          'analysisInfoChanged'
        )
        // TODO:目前这里存在问题
        if (this.middleShow) {
          const middleCircleOptions = {
            renderer: this._map.canvas_track,
            radius: 3,
            weight: 2,
            color: this.circleColor,
            old_color: this.circleColor,
            fill: true,
            fillColor: currentDataPoint.analysisInfo
              ? this.circleLowSogFillColor
              : this.circleFillColor,
            fillOpacity: 1,
            opacity: 1,
            index: i,
            trackid: this.trackid,
          }

          const middleCircleMarker = L.circleMarker(
            [currentDataPoint.lat, currentDataPoint.lng],
            middleCircleOptions
          )

          if (this.isShowTip) {
            middleCircleMarker.on('mouseover', (event) => {
              event.target.setStyle({
                color: this.circleOverColor,
              })

              const prevPoint = processedData[event.target.options.index - 1],
                nextPoint = processedData[event.target.options.index],
                tooltipContent = this.showHtml(
                  nextPoint,
                  prevPoint,
                  this.lableFields
                )

              this.mouseTip.show(event, tooltipContent, 13, 13, 0, 170)

              clearTimeout(this.mouseTipTimer)
              this.mouseTipTimer = setTimeout(() => {
                this.mouseTip.hide()
              }, this.tipHideTimeOut)
            })
            middleCircleMarker.on('mouseout', (event) => {
              event.target.setStyle({
                color: event.target.options.old_color,
              })
              this.mouseTip.hide()
            })
          }

          if (this.pointClick) {
            middleCircleMarker.on('click', (event) => {
              const prevPoint = processedData[event.target.options.index - 1],
                nextPoint = processedData[event.target.options.index],
                clickData = this.showHtml_data(nextPoint, prevPoint)

              setTimeout(() => {
                this.pointClick(clickData, event.target.options.trackid)
              }, 60)
            })
          }

          layer.route.addLayer(middleCircleMarker)
        }

        // 绘制特殊状态标记，如停止、慢速、疑似停止
        if (
          currentDataPoint.analysisInfo &&
          nextDataPoint &&
          currentDataPoint.analysisInfo.type != nextDataPoint.analysisInfo?.type
        ) {
          let markerIconUrl = ''
          if (this.stopShow && currentDataPoint.analysisInfo.type == 'stop') {
            markerIconUrl = this.stopImg
          } else if (
            this.slowShow &&
            currentDataPoint.analysisInfo.type == 'slow'
          ) {
            markerIconUrl = this.slowImg
          } else if (
            this.suspectStopShow &&
            currentDataPoint.analysisInfo.type == 'suspectStop'
          ) {
            markerIconUrl = this.suspectStopImg
          }

          if (markerIconUrl) {
            const markerIcon = L.icon({
              iconUrl: markerIconUrl,
              iconSize: [24, 34],
              iconAnchor: [12, 34],
            })
            const marker = L.marker([nextDataPoint.lat, nextDataPoint.lng], {
              icon: markerIcon,
              _data: currentDataPoint.analysisInfo,
            } as any)

            if (
              this.showTimeType.includes(currentDataPoint.analysisInfo.type)
            ) {
              const tooltipContent =
                currentDataPoint.analysisInfo._stopMight ||
                currentDataPoint.analysisInfo._time
              marker
                .bindTooltip(tooltipContent, {
                  offset: L.point(-10, -22),
                  direction: 'left',
                  className: 'trackStopSlowClass',
                  permanent: true,
                  opacity: 1,
                })
                .openTooltip()
            }

            if (currentDataPoint.analysisInfo.type == 'suspectStop') {
              marker.on('mouseover', (event) => {
                const suspectStopInfo = event.target.options._data,
                  timeStr = `间隔${suspectStopInfo._time
                    .replace('d', '天')
                    .replace('h', '时')
                    .replace('m', '分')
                    .replace('s', '秒')}，航行${suspectStopInfo.md.toFixed(
                    1
                  )}海里<br/>平均速度${suspectStopInfo.sogAvg}节<br/>合理速度${
                    suspectStopInfo.sogMight
                  }节<br/>疑似停靠${suspectStopInfo._stopMight
                    .replace('d', '天')
                    .replace('h', '时')
                    .replace('m', '分')
                    .replace('s', '秒')}`

                this.mouseTip.show(event, timeStr, 13, 13, 0, 190)
              })
              marker.on('mouseout', () => {
                this.mouseTip.hide()
              })
            }

            if (this.pointIconClick) {
              marker.on('click', (event) => {
                this.pointIconClick(event.target.options._data)
              })
            }

            layer.route.addLayer(marker)
          }
        }
      }

      // index++
      // if (this.options.pointDrawEnd) {
      //   this.options.pointDrawEnd(this.trackid, currentDataPoint)
      // }
    }
  }

  /**
   * 坐标点稀疏、显示的点位信息
   * @returns
   */
  calcProcessedData(tracks: any[] = this.tracks, isAppend = false) {
    const trackLength = tracks.length
    console.log(`抽稀前：${trackLength}`)
    // 处理轨迹数据，包括抽稀和分析等
    let processedData = this.dealWrapData(tracks, this.isDilute, isAppend)
    // 计算抽稀后的点数量
    const thinnedPointsLength = processedData.length
    console.log(`抽稀后：${thinnedPointsLength}`)
    // 显示排放数据，如果需要
    if (this.emissionShow && this.emission.show) {
      const emissionLayer = this._showTrackEmission(processedData)
      emissionLayer && layer.route.addLayer(emissionLayer)
      if (this.emission.arrawShow) {
        const emissionArraw = L.polylineDecorator(emissionLayer as any, {
          patterns: [
            {
              offset: 15, // 箭头起始位置
              endOffset: 5,
              repeat: 50, // 箭头重复间隔
              symbol: arrowHeadExtend({
                pixelSize: 20, // 箭头大小
                polygon: true,
                headAngle: 60, // 箭头角度
                pathOptions: {
                  fillOpacity: 0.5,
                  weight: 0,
                  color: this.emission.arrawColor,
                },
              }),
            },
          ],
        })
        emissionArraw && layer.route.addLayer(emissionArraw)
      }
    }

    // 计算绘制轨迹的点
    processedData = this._calcDrawTrackPoints(processedData)
    return processedData
  }

  _calcDrawTrackPoints(trackPoints: any[]) {
    const result = []

    // 检查传入的点是否为空或者长度小于3，如果是，则直接返回处理后的点
    if (!trackPoints || trackPoints.length < 3) {
      return (trackPoints = trackPoints || []).map((point) => {
        const newPoint = clone(point)
        const latLng = this.getLatLng(newPoint)
        newPoint.lat = latLng.lat
        newPoint.lng = latLng.lng
        return newPoint
      })
    }

    // 遍历点集，进行处理
    let prevIndex = -1
    const bounds = this._map.getBounds()
    const length = trackPoints.length
    for (let i = 0; i < length; i++) {
      const currentPoint = clone(trackPoints[i])
      const latLng = this.getLatLng(currentPoint)
      // 保存原始经纬度
      currentPoint.latGPS = currentPoint.lat
      currentPoint.lngGPS = currentPoint.lng
      // 转换为地图坐标
      currentPoint.lat = latLng.lat
      currentPoint.lng = latLng.lng

      // 如果当前点不是数组的第一个或最后一个点，并且它与下一个点不在同一边界内，则跳过
      if (i !== 0 && i !== length - 1) {
        const nextPointLatLng = this.getLatLng(trackPoints[i + 1])
        if (
          nextPointLatLng &&
          !bounds.intersects([
            L.latLng(currentPoint.lat, currentPoint.lng),
            L.latLng(nextPointLatLng.lat, nextPointLatLng.lng),
          ] as any)
        ) {
          if (prevIndex === i - 1) {
            currentPoint.join = false
            result.push(currentPoint)
          }
          continue
        }
      }
      result.push(currentPoint)
      prevIndex = i
    }

    // 调用压缩算法处理点集
    return this._calcDrawTrackPointsZip(result)
  }

  _calcDrawTrackPointsZip(points: any[], dilutionFactor = 1): any[] {
    const numPoints = points.length

    // 如果点的数量小于设定的最大稀释数量，直接返回原始点集
    if (numPoints <= this.diluteMaxCount + 1) {
      return points
    }

    // 计算稀释比例
    const dilutionStep = Math.max(
      2,
      Math.ceil(numPoints / (numPoints - this.diluteMaxCount))
    )
    const result = []

    for (let i = 0; i < numPoints; i++) {
      if (i !== 0 && i !== numPoints - 1) {
        // 根据稀释比例决定是否保留当前点
        if (i % dilutionStep !== 0) {
          result.push(points[i])
        } else {
          // 如果当前点的类型与前一个点不同，并且满足显示条件，则保留当前点
          const currentType = points[i].analysisInfo
            ? points[i].analysisInfo.type
            : ''
          const prevType = points[i - 1].analysisInfo
            ? points[i - 1].analysisInfo.type
            : ''
          if (
            prevType !== currentType &&
            ((this.stopShow && currentType === 'stop') ||
              (this.slowShow && currentType === 'slow') ||
              (this.suspectStopShow && currentType === 'suspectStop'))
          ) {
            result.push(points[i])
          }
        }
      } else {
        // 始终保留第一个和最后一个点
        result.push(points[i])
      }
    }

    // 如果稀释后的点集仍然大于设定的最大数量，递归调用此函数进行进一步稀释
    if (result.length > this.diluteMaxCount + 1 && dilutionFactor <= 5) {
      dilutionFactor++
      return this._calcDrawTrackPointsZip(result, dilutionFactor)
    }

    return result
  }
  /**
   * 显示ai检测
   * @param latlngs
   * @returns
   */
  _showTrackAi(latlngs: any[], appendAiList?: any[]) {
    const zoom = this._map.getZoom()
    const isAppend = appendAiList?.length
    const same = this.oldAiLength === this.aiDetection.datas
    if (this.aiDataCache && this.aiDataCache[zoom] && !isAppend && same) {
      return this.aiDataCache[zoom]
    }
    const sortByData = sortBy(latlngs, ['utc'])
    // 1. 确定在那两段时间中  开始时间 大于等于某个点 结束时间 小于等于某个点
    const getStartEntItem = (time: number, type: 'start' | 'end') => {
      const index = findIndex(sortByData, (item) => {
        const itemUtcTime = item.utc
        return time <= itemUtcTime
      })
      const findItem = find(sortByData, (item) => {
        const itemUtcTime = item.utc
        return time <= itemUtcTime
      })
      if (!Object.keys(findItem).length) {
        console.log('未找到对应节点')
        return { index: 0, item: null }
      }
      // 开始点位
      // 找到相关时间 直接取点位和index
      // 未找到相关时间 小于点位的时间 取前一个点位具体数据和index
      // 结束点位
      // 找到相关时间 直接取点位和index
      // 未找到相关时间 小于点位的时间
      return {
        index:
          findItem.utc === time ? index : type === 'start' ? index - 1 : index,
        item:
          findItem.utc === time
            ? findItem
            : type === 'start'
            ? sortByData[index === 0 ? 0 : index - 1]
            : findItem,
      }
    }

    /**
     *2. 依据两点的时间计算 给定的时间点的坐标点
     * @param startTime 第一个时间点的UTC时间戳
     * @param endTime 第二个时间点的UTC时间戳
     * @param currentTime 给定的时间戳
     */
    const getDistanceLatlng = (
      startItem: { lat?: any; lng?: any; utc?: any },
      endItem: { lat?: any; lng?: any; utc?: any },
      currentTime: number
    ) => {
      const { utc: startTime } = startItem
      const { utc: endTime } = endItem

      const timeFraction = (currentTime - startTime) / (endTime - startTime)
      // 计算给定时间点的经纬度
      const latGiven =
        startItem.lat + (endItem.lat - startItem.lat) * timeFraction
      const lonGiven =
        startItem.lng + (endItem.lng - startItem.lng) * timeFraction

      // 将经纬度转换为地图上的点
      // const pointLatLng = L.latLng(latGiven, lonGiven)
      // var pointGiven = this.map.latLngToContainerPoint(pointLatLng)

      return {
        lat: latGiven,
        lng: lonGiven,
      }
    }

    const appendAiDectionList = isAppend ? appendAiList : this.aiDetection.datas
    let aiDetectionList: any[] = appendAiDectionList.map(
      (item: { startTime: number; endTime: number }) => {
        const { item: startItem, index: startIndex } = getStartEntItem(
          item.startTime,
          'start'
        )
        const { item: endItem, index: endIndex } = getStartEntItem(
          item.endTime,
          'end'
        )
        let completePoint: any[] = []
        if (endIndex - startIndex > 1) {
          completePoint = sortByData
            .slice(startIndex + 1, endIndex)
            .map((item) => ({ ...item, lat: item.lat, lng: item.lng }))
        }
        console.log(startItem, endItem)
        if (!(startItem || endItem)) {
          console.log('给定时间点不在记录的两个时间点之间')
          return []
        }
        // 使用中间点坐标计算两点间坐标
        const startLatLng = getDistanceLatlng(
          startItem,
          completePoint.length ? completePoint[0] : endItem,
          item.startTime
        )
        const endLatLng = getDistanceLatlng(
          completePoint.length
            ? completePoint[completePoint.length - 1]
            : startItem,
          endItem,
          item.endTime
        )

        return {
          ...item,
          startItem,
          endItem,
          startLatLng,
          endLatLng,
          completePoint,
          polyline: [
            [startLatLng.lat, startLatLng.lng],
            ...completePoint.map((item) => [item.lat, item.lng]),
            [endLatLng.lat, endLatLng.lng],
          ],
        }
      }
    )

    aiDetectionList.forEach((item, index) => {
      const aiDetectionLayer = L.polyline(item.polyline, {
        renderer: this._map.canvas_track,
        weight: this.emission.weight,
        color: item.color,
        old_color: item.color,
        useGradient: true,
        interactive: true, // 控制事件是否生效
        opacity: 0.5, // 线透明度
        // dashArray: '10,10', // 虚线样式
        smoothFactor: 2.0, // 平滑因子
        // noClip: false // 是否裁剪
        index,
      } as any)
      aiDetectionLayer && layer.route.addLayer(aiDetectionLayer)
      aiDetectionLayer
        .on('click', (e) => {
          if (this.aiDetection.event && this.aiDetection.event.clickCallback) {
            this.aiDetection.event.clickCallback(
              aiDetectionList[e.target.options.index]
            )
          }
        })
        .on('mouseover', (e: { latlng: L.LatLngExpression; target: any }) => {
          // 可以在这里添加其他的交互，比如显示一个弹窗
          const itemInfo = aiDetectionList[e.target.options.index]
          const { navInfo = {} } = itemInfo
          if (Object.keys(navInfo).length) {
            let content = '<div class="ai-track-popup-customer"><ul>'
            if (itemInfo.startTime)
              content += `<li>开始时间：${
                itemInfo.startTime
                  ? CommUtils.dataFormat(itemInfo.startTime * 1000)
                  : '--'
              }</li>`
            if (itemInfo.endTime)
              content += `<li>结束时间：${
                itemInfo.endTime
                  ? CommUtils.dataFormat(itemInfo.endTime * 1000)
                  : '--'
              }</li>`
            if (navInfo.ATD) content += `<li>ATD 时间：${navInfo.ATD}</li>`
            if (navInfo.ETA) content += `<li>ETA 时间：${navInfo.ETA}</li>`
            if (navInfo.airPressure)
              content += `<li>气压：${navInfo.airPressure}</li>`
            if (navInfo.airTemp) content += `<li>气温：${navInfo.airTemp}</li>`
            if (navInfo.beaufortScale)
              content += `<li>风力等级：${navInfo.beaufortScale}</li>`
            if (navInfo.speedCourse)
              content += `<li>航速航向：${navInfo.speedCourse}</li>`
            if (navInfo.status)
              content += `<li>船舶状态：${navInfo.status}</li>`
            if (navInfo.windSpeed)
              content += `<li>风速：${navInfo.windSpeed}</li>`
            if (navInfo.visibility)
              content += `<li>能见度：${navInfo.visibility}</li>`
            if (navInfo.description)
              content += `<li>描述：${navInfo.description}</li>`
            content += `</ul></div>`
            L.popup({
              closeButton: false,
              className: 'ai-track-popup',
            })
              .setLatLng(e.latlng)
              .setContent(content)
              .openOn(toRaw(this._map))
            e.target
              .setStyle({
                color: this.aiActivityColor,
              })
              .bringToFront()
          }
        })
        .on('mouseout', (event) => {
          event.target
            .setStyle({
              color: event.target.options.old_color,
            })
            .bringToBack()
          // 关闭弹窗或者做其他的清理工作
          this._map.closePopup()
        })
    })
    if (!same && isAppend) {
      aiDetectionList = [...(this.aiDataCache[zoom] || []), ...aiDetectionList]
      this.aiDataCache = {}
    }
    this.aiDataCache[zoom] = aiDetectionList
  }

  _showTrackEmission(latlngs: any[]) {
    return L.polycolor(
      latlngs.map((item) =>
        CanvasShipUtils.GPSEncryptByMapToLatLng([item.lat, item.lng], this._map)
      ),
      {
        renderer: this._map.canvas_track,
        weight: this.emission.weight,
        colors: latlngs.map(
          (item: { emissionCo2Color: any }) =>
            item.emissionCo2Color || this.emission.colorsMap.default
        ),
        useGradient: true,
        interactive: false,
        opacity: 0.5, // 线透明度
        // dashArray: '10,10', // 虚线样式
        smoothFactor: 2.0, // 平滑因子
        // noClip: false // 是否裁剪
      } as any
    )
  }
  setTrackEmissionShow(isShow: any) {
    isShow
      ? !this.emission
        ? (console.info('当前轨迹暂无污染数据'), (this.emissionShow = false))
        : (this.emissionShow = true)
      : (this.emissionShow = false),
      this.showTrack()
  }
  _getStopSlowTrack(startTime: number) {
    const list = this._analysisResultSearch.filter(
      (item: { startTime: number; endTime: number; isShow: boolean }) => {
        return (
          startTime >= item.startTime &&
          startTime <= item.endTime &&
          !item.isShow &&
          ((item.isShow = true), true)
        )
      }
    )
    return list.length > 0 ? list[0] : null
  }
  showHtml(
    currentTrack: any,
    beforeTrack: any,
    unit = ['name', 'mmsi', 'sog', 'cog', 'utc', 'kn']
  ) {
    let html = ''

    const shipInfo = this.showHtml_data(currentTrack, beforeTrack)
    const co2Method = {
      co2: (shipInfo: { emissionCo2Count: number }) => {
        return (
          (shipInfo.emissionCo2Count && shipInfo.emissionCo2Count.toFixed(2)) ||
          null
        )
      },
    } as any
    for (const key of unit) {
      const value =
        (co2Method[key] && co2Method[key](shipInfo)) || shipInfo[key]
      html += this.showHtml_txt(value, key)
    }
    return html
  }
  showHtml_data(
    currentTrack: { kn: number; utc: number },
    beforeTrack: { kn: number; utc: number }
  ) {
    const shipObj = {} as any
    if (this.ship) {
      shipObj.mmsi = this.ship.mmsi
      shipObj.name = this.ship.name
    }
    const shipInfo = L.Util.extend({}, currentTrack, shipObj)
    shipInfo.utc = CommUtils.dataFormat(shipInfo.utc * 1000)
    shipInfo.sog && (shipInfo.sog = Number(shipInfo.sog).toFixed(2))
    beforeTrack &&
      (shipInfo.sog2 = Number(
        ((currentTrack.kn - beforeTrack.kn) /
          (currentTrack.utc - beforeTrack.utc)) *
          3600
      ).toFixed(2))
    shipInfo.kn && (shipInfo.kn = Number(shipInfo.kn).toFixed(1))
    shipInfo.ed &&
      shipInfo.ed > 0 &&
      (shipInfo.ed = Number(shipInfo.ed).toFixed(1))
    shipInfo.md &&
      shipInfo.md > 0 &&
      (shipInfo.md = Number(shipInfo.md).toFixed(1))
    shipInfo.cog &&
      shipInfo.cog > 0 &&
      (shipInfo.cog = Number(shipInfo.cog).toFixed(0))
    shipInfo.draught &&
      shipInfo.draught > 0 &&
      (shipInfo.draught = Number(shipInfo.draught).toFixed(1))
    shipInfo.rot &&
      shipInfo.rot > 0 &&
      (shipInfo.rot = Number(shipInfo.rot).toFixed(1))
    shipInfo.navistatus &&
      shipInfo.navistatus >= 0 &&
      (shipInfo.navistatus = CanvasShipUtils.getDisValue(
        shipInfo.navistatus,
        'naviStatus',
        'zh_CN'
      ))
    shipInfo._lat = shipInfo.lat
    shipInfo._lng = shipInfo.lng
    shipInfo.lat = CanvasShipUtils.latFormatter(shipInfo.lat)
    shipInfo.lng = CanvasShipUtils.lngFormatter(shipInfo.lng)
    return shipInfo
  }
  showHtml_txt(value: string, key: string) {
    let text = ''
    CommUtils.isEmpty(value) ||
      CommUtils.isEmpty(this.showHtml_txt_templater[key]) ||
      (text = this.showHtml_txt_templater[key].replace('{1}', value))
    return text
  }
  _on_baselayerchange() {
    this.isShowTrack = true
    this._map.off('moveend', this._Event.moveend)
    this.showTrack()
    this._map.on('moveend', this._Event.moveend)
  }
  /**
   * 坐标点稀释
   * @param trackData 轨迹数据
   * @param isDilute 是否开启稀释
   * @param zoomLevel
   * @returns
   */
  dealWrapData(
    trackData: any[],
    isDilute: boolean,
    isAppend = false,
    zoomLevel = 1
  ) {
    const zoom = this._map.getZoom()
    // 判断老的长度是否和 内存数据一致 走缓存 如果不一致 则往缓存中追加数据
    const same = this.oldTrackLength === this.tracks.length
    if (
      this.diluteDataCache &&
      this.diluteDataCache[zoom] &&
      !isAppend &&
      same
    ) {
      return this.diluteDataCache[zoom]
    }
    let result: any[] = []
    const dataLength = trackData ? trackData.length : 0

    // 如果数据点少于2个，则不需要处理
    if (dataLength < 2) {
      return trackData
    }

    // 如果只有2个数据点，标记为起始和结束点
    if (dataLength === 2) {
      trackData[0].isStartLatLng = true
      trackData[dataLength - 1].isEndLatLng = true
      return trackData
    }

    // 如果启用了稀释功能
    if (isDilute) {
      if (this.isDiluteType === 1) {
        // 稀释类型1
        let distanceThreshold = 30 // 默认距离阈值
        switch (true) {
          case zoom >= 18:
            distanceThreshold = 100
            break
          case zoom >= 7:
            distanceThreshold = 50
            break
          case zoom >= 6:
          case zoom >= 5:
            distanceThreshold = 60
            break
          default:
            distanceThreshold = 30
        }
        for (let i = 0; i < dataLength; i++) {
          const currentData = trackData[i]
          if (!(i === 0 || i === dataLength - 1)) {
            let distance = distanceThreshold * zoomLevel
            const previousData = result[result.length - 1]
            const currentLatLng = L.latLng(
              this.getLat(currentData),
              this.getLng(currentData)
            )
            const previousLatLng = L.latLng(
              this.getLat(previousData),
              this.getLng(previousData)
            )
            const layerPointCurrent =
              this._map.latLngToLayerPoint(currentLatLng)
            const layerPointPrevious =
              this._map.latLngToLayerPoint(previousLatLng)
            const deltaX = Math.abs(layerPointCurrent.x - layerPointPrevious.x)
            const deltaY = Math.abs(layerPointCurrent.y - layerPointPrevious.y)
            const maxDistance = Math.max(deltaX, deltaY)
            if (Math.abs(currentData.cog - previousData.cog) < 10) {
              distance = Math.max(100, distance)
            }
            const isClose = this.dealWrapDataFilter(currentData, previousData)
            if (!(maxDistance >= distance || isClose)) {
              if (
                maxDistance < distance &&
                currentData.sog === 0 &&
                result.length > 2
              ) {
                result[result.length - 1] = currentData
              }
              continue
            }
          }
          result.push(currentData)
        }
      }
      if (this.isDiluteType === 2) {
        // 稀释类型2
        let threshold = 1800 // 默认阈值
        switch (zoom) {
          case 1:
          case 2:
            threshold *= 50
            break
          case 3:
            threshold *= 25
            break
          case 4:
            threshold *= 10
            break
          case 5:
          case 6:
            threshold *= 5
            break
          case 7:
            threshold *= 3
            break
          case 8:
            threshold *= 1.5
            break
          case 9:
            threshold *= 1
            break
          case 10:
            threshold *= 0.7
            break
          case 11:
            threshold *= 0.5
            break
          case 12:
            threshold *= 0.05
            break
          case 13:
            threshold *= 0.02
            break
          case 14:
            threshold *= 0.005
            break
          case 15:
            threshold *= 0.0005
            break
          case 16:
            threshold *= 0.0002
            break
          case 17:
            threshold *= 0.0001
            break
          default:
            threshold = 0
        }
        result =
          threshold * zoomLevel > 0
            ? Douglas.douglasPeucker(trackData, threshold * zoomLevel)
            : result.concat(trackData)
      }
    } else {
      result = result.concat(trackData)
    }

    // 确保起始和结束点被标记
    if (result.length > 0) {
      result[0].isStartLatLng = true
      result[result.length - 1].isEndLatLng = true
    }

    result = result || []

    if (!same && isAppend) {
      result = [...(this.diluteDataCache[zoom] || []), ...result]
      this.diluteDataCache = {}
    }

    // 缓存稀释后的数据
    this.diluteDataCache[zoom] = result
    return result
  }

  dealWrapDataFilter(
    currentItem: { analysisInfo: { type: any }; from: number; isKeep: number },
    compareItem: { analysisInfo: { type: any }; from: number }
  ) {
    let shouldFilterOut = false
    const currentAnalysisType = currentItem.analysisInfo
      ? currentItem.analysisInfo.type
      : ''
    const compareAnalysisType = compareItem.analysisInfo
      ? compareItem.analysisInfo.type
      : ''

    if (
      (this.stopShow &&
        'stop' === currentAnalysisType &&
        compareAnalysisType !== currentAnalysisType) ||
      (this.slowShow &&
        'slow' === currentAnalysisType &&
        compareAnalysisType !== currentAnalysisType) ||
      (this.suspectStopShow && 'suspectStop' === currentAnalysisType) ||
      (2 === currentItem.from && 2 !== compareItem.from) ||
      1 === currentItem.isKeep
    ) {
      shouldFilterOut = true
    }

    return shouldFilterOut
  }

  getLng(coordinates: { lng: any; lon: any }) {
    if (coordinates) return coordinates.lng ? coordinates.lng : coordinates.lon
  }
  getLat(coordinates: { lat: any }) {
    if (coordinates) return coordinates.lat
  }
  getLatLng(point: any) {
    return this._map.offsetC
      ? this.getOffsetLatLng({
          lat: point.lat,
          lng: 'lng' in point ? point.lng : point.lon,
        })
      : {
          lat: point.lat,
          lng: 'lng' in point ? point.lng : point.lon,
        }
  }
  getLineDir(
    startCoordinates: L.LatLngExpression,
    endCoordinates: L.LatLngTuple | { lat: any; lng: any } | null
  ) {
    if (endCoordinates) {
      const angleDegrees = CanvasShipUtils.getAngleByLatLng(
        startCoordinates,
        endCoordinates
      )
      return (angleDegrees >= 22.5 && angleDegrees < 67.5) ||
        (angleDegrees >= 202.5 && angleDegrees < 270)
        ? 2
        : 1
    }
    return 2
  }
  getTrackLabelPath1(latAndLng: { lat: any; lng: any }, type: number) {
    let latlngs, rotatePoint
    const zoom = this._map.getZoom()
    const scan = 1.2 / 2 ** zoom
    switch (type) {
      case 2:
        latlngs = [
          [0, 0],
          [-20, 20],
        ]
        break
      case 3:
        latlngs = [
          [0, 0],
          [-20, -20],
        ]
        break
      case 4:
        latlngs = [
          [0, 0],
          [20, -20],
        ]
        break
      default:
        latlngs = [
          [0, 0],
          [20, 20],
        ]
    }
    for (const latlng of latlngs) {
      rotatePoint = this.getRotatePoint(0, 0, latlng[0], latlng[1], 0)
      latlng[0] = latAndLng.lat + rotatePoint.x * scan
      latlng[1] = latAndLng.lng + rotatePoint.y * scan
    }
    return latlngs
  }
  getTrackLabelPath2(
    latAndLng: { lat: any; lng: any },
    lng: number,
    type: number
  ) {
    let latlngs, latlng, rotatePoint
    const zoom = this._map.getZoom()
    const scan = 1.2 / 2 ** zoom
    switch (type) {
      case 2:
        latlngs = [
          [-20, 20],
          [-20, lng],
        ]
        break
      case 3:
        latlngs = [
          [-20, -lng],
          [-20, -20],
        ]
        break
      case 4:
        latlngs = [
          [20, -lng],
          [20, -20],
        ]
        break
      default:
        latlngs = [
          [20, 20],
          [20, lng],
        ]
    }
    for (const latlng of latlngs) {
      rotatePoint = this.getRotatePoint(0, 0, latlng[0], latlng[1], 0)
      latlng[0] = latAndLng.lat + rotatePoint.x * scan
      latlng[1] = latAndLng.lng + rotatePoint.y * scan
    }
    return latlngs
  }
  setAreaPath(centerCoordinates: L.LatLngExpression, width: number) {
    const centerPoint = this._map.latLngToLayerPoint(centerCoordinates)
    const leftPoint = this._map.layerPointToLatLng(
      L.point(centerPoint.x - width / 2, centerPoint.y)
    )
    const rightPoint = this._map.layerPointToLatLng(
      L.point(centerPoint.x + width / 2, centerPoint.y)
    )

    return {
      polygon: L.polyline(
        [
          [leftPoint.lat, leftPoint.lng],
          [rightPoint.lat, rightPoint.lng],
        ],
        {
          renderer: this._map.canvas_track,
          color: 'transparent', // Assuming you want no stroke color, but making it transparent for clarity
          weight: 0,
          fill: false,
          opacity: 0,
        }
      ),
      offset: {
        x: centerPoint.x - width / 2,
        y: centerPoint.y,
      },
    }
  }
  getRotatePoint(
    originalX: number,
    originalY: number,
    pointX: number,
    pointY: number,
    angleDegrees: number
  ) {
    const angleRadians = (angleDegrees * Math.PI) / 180
    return {
      x:
        (pointX - originalX) * Math.cos(angleRadians) -
        (pointY - originalY) * Math.sin(angleRadians) +
        originalX,
      y:
        (pointY - originalY) * Math.cos(angleRadians) +
        (pointX - originalX) * Math.sin(angleRadians) +
        originalY,
    }
  }

  addLabelCache(lineCoordinates: any[][]) {
    const startPoint = [lineCoordinates[0][0], lineCoordinates[0][1]]
    const endPoint = [lineCoordinates[1][0], lineCoordinates[1][1]]
    const startPointLayer = this._map.latLngToLayerPoint(
      this.toLatlng([startPoint[0], startPoint[1]])
    )
    const endPointLayer = this._map.latLngToLayerPoint(
      this.toLatlng([endPoint[0], endPoint[1]])
    )
    const labelLine = [
      { x: endPointLayer.x, y: endPointLayer.y + 10 },
      { x: startPointLayer.x, y: startPointLayer.y - 10 },
    ]

    if (this.labelCache.length > 0) {
      for (const existingLines of this.labelCache) {
        if (this.idCrossLine(labelLine, existingLines)) return false
      }
      this.labelCache.push(labelLine)
      return true
    }
    this.labelCache.push(labelLine)
    return true
  }
  toLatlng(coordinates: number[]) {
    return new L.LatLng(coordinates[0], coordinates[1])
  }
  resetCache() {
    this.aiDataCache = {}
    this.diluteDataCache = {}
    this.showTrack()
  }
  idCrossLine(line1: any, line2: any) {
    // line1 和 line2 是两个线段的端点数组，每个端点包含 x 和 y 坐标
    // line1[0] 和 line1[1] 是线段1的起点和终点
    // line2[0] 和 line2[1] 是线段2的起点和终点

    // 计算线段的中点
    const mid1 = {
      x: (line1[0].x + line1[1].x) / 2,
      y: (line1[0].y + line1[1].y) / 2,
    }
    const mid2 = {
      x: (line2[0].x + line2[1].x) / 2,
      y: (line2[0].y + line2[1].y) / 2,
    }

    // 计算线段的边距（线段到中点的水平和垂直距离）
    const dist1X = Math.abs(mid1.x - line1[0].x)
    const dist2X = Math.abs(mid2.x - line2[0].x)
    const dist1Y = Math.abs(mid1.y - line1[0].y)
    const dist2Y = Math.abs(mid2.y - line2[0].y)

    // 判断两条线段是否相交
    // 如果中点之间的距离小于各自到端点的距离，则线段相交
    return (
      Math.abs(mid1.x - mid2.x) < Math.min(dist1X, dist2X) &&
      Math.abs(mid1.y - mid2.y) < Math.min(dist1Y, dist2Y)
    )
  }
  addTrackManager(labelPath: any[][]) {
    const startPoint = [labelPath[0][0], labelPath[0][1]]
    const endPoint = [labelPath[1][0], labelPath[1][1]]

    // 将经纬度坐标转换为图层坐标点
    const startPointLayer = this._map.latLngToLayerPoint(
      this.toLatlng(startPoint)
    )
    const endPointLayer = this._map.latLngToLayerPoint(this.toLatlng(endPoint))

    // 创建标签路径点数组
    const labelPathPoints = [
      {
        x: endPointLayer.x,
        y: endPointLayer.y + 10, // 向右偏移10个像素
      },
      {
        x: startPointLayer.x,
        y: startPointLayer.y - 10, // 向下偏移10个像素
      },
    ]

    // 如果标签缓存中已经有元素
    if (this.labelCache.length > 0) {
      for (let i = 0; i < this.labelCache.length; i++) {
        const cacheItem = this.labelCache[i] // _0x3f65b2
        // 如果新标签路径与缓存中的任一项相交，则返回false
        if (this.idCrossLine(labelPathPoints, cacheItem)) {
          return false
        }
      }
      // 如果没有相交，添加新标签路径到缓存，并返回true
      this.labelCache.push(labelPathPoints)
      return true
    }
    // 如果标签缓存为空，添加新标签路径到缓存，并返回true
    this.labelCache.push(labelPathPoints)
    return true
  }

  getOffsetLatLng(coordinate: { lat: any; lng: any }) {
    const encryptedCoordinate = GPS.gcj_encrypt(coordinate.lat, coordinate.lng)
    return {
      lat: encryptedCoordinate.lat,
      lng: encryptedCoordinate.lon,
    }
  }
  /**
   * 追加航线分段加载
   */
  appendLine(tracks: any[], aiDetectionList?: []) {
    this.tracks = [...toRaw(this.tracks), ...tracks.slice(1, tracks.length)]
    const processedData = this.calcProcessedData(tracks, true)
    this.oldTrackLength = this.tracks.length
    // 显示ai检测船舶数据
    if (
      this.aiDetectionShow &&
      this.aiDetection.show &&
      aiDetectionList &&
      aiDetectionList.length
    ) {
      this.aiDetection.datas = [...this.aiDetection.datas, ...aiDetectionList]
      this._showTrackAi(processedData, aiDetectionList)
      this.oldAiLength = this.aiDetection.datas.length
    }
    this.drawLine(processedData)
  }
}
const trackSymbol = (map: MyMap, data: any[], ship: any, options: DataObj) => {
  return new TrackSymbolImpl(map, data, ship, options)
}

export { trackSymbol, TrackSymbolImpl }
